import {
  Card,
  Divider,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Snackbar,
  Alert as MuiAlert,
  TableContainer,
  CardContent,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab
} from '@mui/material'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow,
  ShowNoRecordsFound,
  ErrorScreen,

} from '../../utils/ui_utils'
import moment from 'moment'
import { useEffect, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck, isEmpty } from '../../utils/utils'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enLocale from 'date-fns/locale/en-IN'
import { useDispatch } from 'react-redux'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import { useFetchWaiterCommissionReport } from '../../modules/reports/reportsHooks.ts'
import { clearWaiterCommissionState } from '../../modules/reports/reportsSlice.ts'
import { ReportService } from '../../modules/reports/service/reportsService.ts'
import { APIPATHENV } from '../../utils/tsUtils/Env'
import * as XLSX from 'xlsx'
import { BarChart } from '../../components/Chart'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'
import useReportRefresh from '../../hooks/useReportRefresh'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'

var JSONbig = require('json-bigint')({ useNativeBigInt: true })

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const WaiterCommissionReport = () => {
  // Common states
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(15)
  const [pagecount, setPageCount] = useState(1)
  const [selectedOutlet, setSelectedOutlet] = useState('')
  // Commission mode filter: 0 = All, 1 = Item Sale, 2 = Value Sale
  const [commissionMode, setCommissionMode] = useState(0)
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [fromDate, setFromDate] = useState(moment().startOf('day'))
  const [toDate, setToDate] = useState(moment().startOf('day'))
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showToDateError, setShowToDateError] = useState(false)
  const [toDateErrorMessage, setToDateErrorMessage] = useState(
    'To Date is earlier than From Date'
  )
  const [goToPageInput, setGoToPageInput] = useState('')

  // Outlet data loaded via Redux hook

  // Report data states
  const [commissionData, setCommissionData] = useState([])
  const [reportVisibility, setReportVisibility] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  // 'All' mode: split rows by mode (1 = item sale, 2 = value sale)
  // Loaded via two separate fetches when commissionMode === 0.
  const [itemSaleRows, setItemSaleRows] = useState([])
  const [valueSaleRows, setValueSaleRows] = useState([])
  const [itemSaleAggregate, setItemSaleAggregate] = useState({ totalSales: 0, totalCommAmt: 0 })
  const [valueSaleAggregate, setValueSaleAggregate] = useState({ totalSales: 0, totalCommAmt: 0 })
  const [singleAggregate, setSingleAggregate] = useState({ totalSales: 0, totalCommAmt: 0 })
  const [allModeTab, setAllModeTab] = useState(0) // 0 = Item Sale, 1 = Value Sale
  const [allModeLoading, setAllModeLoading] = useState(false)
  const [graphData, setGraphData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Commission Amount',
        data: [],
        backgroundColor: ['#e0218a'],
        barThickness: 30
      }
    ]
  })

  const history = useNavigate()
  const dispatch = useDispatch()
  const { outletFetch, outletFetchData, outletFetchLoading } = useFetchOutlet()
  const { waiterCommissionReportLoading, waiterCommissionReportData, waiterCommissionReportFetch } = useFetchWaiterCommissionReport()

  // Outlet list excludes the "All Outlets" aggregate entry
  const outlets = (outletFetchData?.data || []).filter(
    (o) => String(o?._id) !== '0' && o?.name !== 'All Outlets'
  )

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity = 'success') => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    fetchReportData(value)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput, 10)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      handlePageChange(null, pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const pageCount = (total) => {
    let count = Math.ceil(total / limit)
    setPageCount(count)
  }

  // Fetch outlets
  const fetchOutlets = async () => {
    await outletFetch({ pageNumber: 1, limit: 200 })
  }

  // Fetch report data
  const fetchReportData = async (pageNum = page) => {
    const tempFromDate = moment(fromDate)
    const tempToDate = moment(toDate)

    if (tempFromDate.isAfter(tempToDate)) {
      toastMessage('From Date cannot be after To Date', 'error')
      return
    }

    if (isEmpty(selectedOutlet)) {
      toastMessage('Please select an outlet', 'error')
      return
    }

    const fromDateStr = moment(fromDate).format('YYYY-MM-DD')
    const toDateStr = moment(toDate).add(1, 'days').format('YYYY-MM-DD')

    setHasFetched(true)

    // 'All' mode: fetch item sale (mode=1) and value sale (mode=2) separately,
    // so the UI can show them in distinct tabs.
    if (Number(commissionMode) === 0) {
      try {
        setAllModeLoading(true)
        setItemSaleRows([])
        setValueSaleRows([])
        setItemSaleAggregate({ totalSales: 0, totalCommAmt: 0 })
        setValueSaleAggregate({ totalSales: 0, totalCommAmt: 0 })
        const service = new ReportService(APIPATHENV)
        const [itemRes, valueRes] = await Promise.all([
          service.fetchWaiterCommissionReport({
            from_date: fromDateStr,
            to_date: toDateStr,
            outlet_id: selectedOutlet,
            pageNumber: pageNum,
            limit,
            commission_mode: 1
          }),
          service.fetchWaiterCommissionReport({
            from_date: fromDateStr,
            to_date: toDateStr,
            outlet_id: selectedOutlet,
            pageNumber: pageNum,
            limit,
            commission_mode: 2
          })
        ])
        setItemSaleRows(itemRes?.data || [])
        setValueSaleRows(valueRes?.data || [])
        setItemSaleAggregate({
          totalSales: itemRes?.aggregate?.TotalSales || 0,
          totalCommAmt: itemRes?.aggregate?.TotalCommAmt || 0
        })
        setValueSaleAggregate({
          totalSales: valueRes?.aggregate?.TotalSales || 0,
          totalCommAmt: valueRes?.aggregate?.TotalCommAmt || 0
        })
        setReportVisibility(
          (itemRes?.data?.length || 0) > 0 ||
            (valueRes?.data?.length || 0) > 0
        )
      } catch (e) {
        console.error('Error fetching all-mode commission data:', e)
        toastMessage('Error fetching report', 'error')
      } finally {
        setAllModeLoading(false)
      }
      return
    }

    await waiterCommissionReportFetch({
      from_date: fromDateStr,
      to_date: toDateStr,
      outlet_id: selectedOutlet,
      pageNumber: pageNum,
      limit,
      commission_mode: commissionMode
    })
  }

  // Initialize
  useEffect(() => {
    businessCheck(history)
    fetchOutlets()
    return () => {
      dispatch(clearWaiterCommissionState())
    }
  }, [])

  // Update selected outlet when outlet data loads
  useEffect(() => {
    if (outlets.length > 0 && isEmpty(selectedOutlet)) {
      setSelectedOutlet(outlets[0]._id)
    }
  }, [outletFetchData])

  // Update commission report data (single-mode only; 'all' mode is handled
  // directly in fetchReportData via two parallel service calls)
  useEffect(() => {
    if (Number(commissionMode) === 0) return
    if (waiterCommissionReportData) {
      setSingleAggregate({
        totalSales: waiterCommissionReportData.aggregate?.TotalSales || 0,
        totalCommAmt: waiterCommissionReportData.aggregate?.TotalCommAmt || 0
      })
      if (waiterCommissionReportData.data && waiterCommissionReportData.data.length > 0) {
        setCommissionData(waiterCommissionReportData.data)
        pageCount(waiterCommissionReportData.total)
        setReportVisibility(true)

        // Update graph data
        setGraphData({
          labels: waiterCommissionReportData.data.map((row) => row.WaiterName),
          datasets: [
            {
              label: 'Commission Amount (₹)',
              data: waiterCommissionReportData.data.map((row) => row.CommAmt || 0),
              backgroundColor: ['#e0218a'],
              barThickness: 30
            }
          ]
        })
      } else {
        setCommissionData([])
        setReportVisibility(false)
        setGraphData({
          labels: [],
          datasets: [
            {
              label: 'Commission Amount',
              data: [],
              backgroundColor: ['#e0218a'],
              barThickness: 30
            }
          ]
        })
      }
    }
  }, [waiterCommissionReportData])

  // Export to Excel
  const exportToExcel = () => {
    const isAllMode = Number(commissionMode) === 0
    const isItemSale = Number(commissionMode) === 1
    const filename = 'Waiter_Commission_Report'

    // 'All' mode uses two sections with different column counts (Item Sale = 3 cols,
    // Value Sale = 4 cols), so we build the sheet as an array-of-arrays.
    if (isAllMode) {
      const aoa = []
      aoa.push(['Waiter Commission Report'])
      aoa.push([])

      aoa.push(['Item Sale'])
      aoa.push(['Waiter Name', 'Total Sales', 'Commission Amount'])
      if (itemSaleRows.length === 0) {
        aoa.push(['No records found'])
      } else {
        itemSaleRows.forEach((row) => {
          aoa.push([
            row.WaiterName ?? '-',
            row.TotalSales ?? '-',
            row.CommAmt ?? '-'
          ])
        })
        aoa.push([
          'Total',
          itemSaleAggregate.totalSales ?? '-',
          itemSaleAggregate.totalCommAmt ?? '-'
        ])
      }

      aoa.push([])

      aoa.push(['Value Sale'])
      aoa.push(['Waiter Name', 'Total Sales', 'Commission %', 'Commission Amount'])
      if (valueSaleRows.length === 0) {
        aoa.push(['No records found'])
      } else {
        valueSaleRows.forEach((row) => {
          aoa.push([
            row.WaiterName ?? '-',
            row.TotalSales ?? '-',
            row.CommPer ?? '-',
            row.CommAmt ?? '-'
          ])
        })
        aoa.push([
          'Total',
          valueSaleAggregate.totalSales ?? '-',
          '-',
          valueSaleAggregate.totalCommAmt ?? '-'
        ])
      }

      try {
        const ws = XLSX.utils.aoa_to_sheet(aoa)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Report')
        XLSX.writeFile(wb, `${filename}_${moment().format('YYYY-MM-DD')}.xlsx`)
        toastMessage('Report exported successfully')
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        toastMessage('Error exporting report', 'error')
      }
      return
    }

    // Single-mode export: Item Sale hides Commission %, Value Sale shows it.
    const headers = isItemSale
      ? [
          { field: 'WaiterName', title: 'Waiter Name' },
          { field: 'TotalSales', title: 'Total Sales' },
          { field: 'CommAmt', title: 'Commission Amount' }
        ]
      : [
          { field: 'WaiterName', title: 'Waiter Name' },
          { field: 'TotalSales', title: 'Total Sales' },
          { field: 'CommPer', title: 'Commission %' },
          { field: 'CommAmt', title: 'Commission Amount' }
        ]

    const data = [...commissionData]
    if (commissionData.length > 0) {
      data.push({
        WaiterName: 'TOTAL',
        TotalSales: singleAggregate.totalSales ?? '-',
        CommPer: '-',
        CommAmt: singleAggregate.totalCommAmt ?? '-'
      })
    }

    try {
      const ws = XLSX.utils.json_to_sheet(
        data.map((row) => {
          const newRow = {}
          headers.forEach((h) => {
            newRow[h.title] = row[h.field] ?? '-'
          })
          return newRow
        })
      )
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Report')
      XLSX.writeFile(wb, `${filename}_${moment().format('YYYY-MM-DD')}.xlsx`)
      toastMessage('Report exported successfully')
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      toastMessage('Error exporting report', 'error')
    }
  }


  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Waiter Name',
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Commission Amount (₹)',
          font: {
            weight: 'bold'
          }
        },
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      datalabels: {
        display: true,
        color: 'black',
        formatter: Math.round,
        anchor: 'end',
        offset: -20,
        align: 'start'
      }
    }
  }

  // Refresh functionality
  const refreshFunction = () => fetchReportData(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh } = useReportRefresh(
    refreshFunction,
    [page, selectedOutlet, fromDate, toDate, commissionMode]
  )

  // Auto-fetch report when filters change (NO waiter selector for commission report)
  useEffect(() => {
    if (!isEmpty(selectedOutlet)) {
      setPage(1)
      fetchReportData(1)
    }
  }, [selectedOutlet, fromDate, toDate, commissionMode])

  return (
    <Box sx={{ p: 2 }}>
      <Helmet title="Waiter Commission Report" />

      {/* Header */}
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline">
            Waiter Commission Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={waiterCommissionReportLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mb: 3 }} />

      {/* Filters */}
      <MainCard sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Outlet Selector */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Outlet</InputLabel>
                <Select
                  value={selectedOutlet}
                  onChange={(e) => setSelectedOutlet(e.target.value)}
                  label="Outlet"
                  disabled={outletFetchLoading}
                >
                  {outlets.map((outlet) => (
                    <MenuItem key={outlet._id} value={outlet._id}>
                      {outlet.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Commission Mode Selector */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Commission Mode</InputLabel>
                <Select
                  value={commissionMode}
                  onChange={(e) => setCommissionMode(e.target.value)}
                  label="Commission Mode"
                >
                  <MenuItem value={0}>All</MenuItem>
                  <MenuItem value={1}>Item Sale</MenuItem>
                  <MenuItem value={2}>Value Sale</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            {/* From Date */}
            <Grid item xs={12} sm={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
                <DatePicker
                  label="From Date"
                  value={fromDate.toDate()}
                  onChange={(newValue) => {
                    setFromDate(moment(newValue))
                    if (moment(toDate).isBefore(newValue)) {
                      setShowToDateError(true)
                    } else {
                      setShowToDateError(false)
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={showError}
                      helperText={showError ? errorMessage : ''}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* To Date */}
            <Grid item xs={12} sm={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
                <DatePicker
                  label="To Date"
                  value={toDate.toDate()}
                  onChange={(newValue) => {
                    setToDate(moment(newValue))
                    if (moment(fromDate).isAfter(newValue)) {
                      setShowToDateError(true)
                    } else {
                      setShowToDateError(false)
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={showToDateError}
                      helperText={showToDateError ? toDateErrorMessage : ''}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </CardContent>
      </MainCard>

      {/* Report Display */}
      {(waiterCommissionReportLoading || allModeLoading) && (
        <Grid container spacing={10} direction={'column'} sx={{ paddingTop: 5 }}>
          <Grid item>
            <Grid container rowSpacing={4.5} columnSpacing={10} sx={{ mt: 1 }}>
              <Grid item xs sm md={8}>
                <MainCard content={false} sx={{ mt: 1.5 }}>
                  <Box sx={{ pt: 1, pr: 2, pl: 2, pb: 1 }}>
                    <ListTypeShimmer />
                  </Box>
                </MainCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!waiterCommissionReportLoading && !allModeLoading && reportVisibility && (
        <>

          {/* Export Button */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Export to Excel" placement="top" arrow>
              <IconButton variant="contained" onClick={exportToExcel} size="large">
                <CloudUploadIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Graph (single mode only — 'all' mode shows tabs instead) */}
          {Number(commissionMode) !== 0 && graphData.labels.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Waiter Commission Overview
                </Typography>
                <Box sx={{ height: '400px' }}>
                  <BarChart chartData={graphData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Report Table */}
          {Number(commissionMode) === 0 ? (
            // 'All' mode: tabs for Item Sale / Value Sale
            <Card>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={allModeTab}
                  onChange={(_, v) => setAllModeTab(v)}
                  textColor="secondary"
                  indicatorColor="secondary"
                >
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
                        <span>Item Sale</span>
                      </Box>
                    }
                  />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1565c0' }} />
                        <span>Value Sale</span>
                      </Box>
                    }
                  />
                </Tabs>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell>Waiter Name</StyledTableCell>
                      <StyledTableCell align="right">Total Sales</StyledTableCell>
                      {/* Item Sale tab hides Commission % (per-item rates) */}
                      {allModeTab === 1 && (
                        <StyledTableCell align="right">Commission %</StyledTableCell>
                      )}
                      <StyledTableCell align="right">Commission Amount</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {/* Section total row (per-tab) */}
                    <TableRow
                      sx={{
                        backgroundColor: allModeTab === 0 ? '#e8f5e9' : '#e3f2fd'
                      }}
                    >
                      <StyledTableCell
                        sx={{
                          fontWeight: 700,
                          color: allModeTab === 0 ? '#1b5e20' : '#0d47a1'
                        }}
                      >
                        {allModeTab === 0 ? 'Item Sale Total' : 'Value Sale Total'}
                      </StyledTableCell>
                      <StyledTableCell sx={{ fontWeight: 700 }} align="right">
                        ₹{(allModeTab === 0
                          ? itemSaleAggregate.totalSales
                          : valueSaleAggregate.totalSales
                        ).toFixed(2)}
                      </StyledTableCell>
                      {allModeTab === 1 && (
                        <StyledTableCell sx={{ fontWeight: 700 }} align="right">
                          -
                        </StyledTableCell>
                      )}
                      <StyledTableCell sx={{ fontWeight: 700 }} align="right">
                        ₹{(allModeTab === 0
                          ? itemSaleAggregate.totalCommAmt
                          : valueSaleAggregate.totalCommAmt
                        ).toFixed(2)}
                      </StyledTableCell>
                    </TableRow>
                    {(allModeTab === 0 ? itemSaleRows : valueSaleRows).length === 0 && (
                      <TableRow>
                        <StyledTableCell colSpan={4} align="center">
                          No records found
                        </StyledTableCell>
                      </TableRow>
                    )}
                    {(allModeTab === 0 ? itemSaleRows : valueSaleRows).map((row, i) => (
                      <StyledTableRow key={i}>
                        <StyledTableCell>{row.WaiterName}</StyledTableCell>
                        <StyledTableCell align="right">
                          ₹{row.TotalSales?.toFixed(2)}
                        </StyledTableCell>
                        {allModeTab === 1 && (
                          <StyledTableCell align="right">
                            {row.CommPer}%
                          </StyledTableCell>
                        )}
                        <StyledTableCell align="right">
                          ₹{row.CommAmt?.toFixed(2)}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            // Single mode: standard table; Item Sale hides Commission %
            <TableContainer component={Card}>
              <Table size="small">
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell>Waiter Name</StyledTableCell>
                    <StyledTableCell align="right">Total Sales</StyledTableCell>
                    {Number(commissionMode) !== 1 && (
                      <StyledTableCell align="right">Commission %</StyledTableCell>
                    )}
                    <StyledTableCell align="right">Commission Amount</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {/* Total row (mirrors Flutter app) */}
                  <TableRow sx={{ backgroundColor: '#ECF9FF' }}>
                    <StyledTableCell sx={{ fontWeight: 700 }}>Total:</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 700 }} align="right">
                      ₹{singleAggregate.totalSales.toFixed(2)}
                    </StyledTableCell>
                    {Number(commissionMode) !== 1 && (
                      <StyledTableCell sx={{ fontWeight: 700 }} align="right">-</StyledTableCell>
                    )}
                    <StyledTableCell sx={{ fontWeight: 700 }} align="right">
                      ₹{singleAggregate.totalCommAmt.toFixed(2)}
                    </StyledTableCell>
                  </TableRow>
                  {commissionData.map((row, i) => (
                    <StyledTableRow key={i}>
                      <StyledTableCell>{row.WaiterName}</StyledTableCell>
                      <StyledTableCell align="right">
                        ₹{row.TotalSales?.toFixed(2)}
                      </StyledTableCell>
                      {Number(commissionMode) !== 1 && (
                        <StyledTableCell align="right">{row.CommPer}%</StyledTableCell>
                      )}
                      <StyledTableCell align="right">
                        ₹{row.CommAmt?.toFixed(2)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination (single mode only) */}
          {Number(commissionMode) !== 0 && pagecount > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                mt: 2
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Pagination
                  count={pagecount}
                  page={page}
                  color="secondary"
                  size="small"
                  boundaryCount={2}
                  siblingCount={0}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      transition: 'all 0.2s ease-in-out'
                    }
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: 'white',
                  p: 2,
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Jump to
                </Typography>
                <TextField
                  size="small"
                  value={goToPageInput}
                  onChange={handleGoToPageChange}
                  onKeyPress={handleGoToPageKeyPress}
                  placeholder="Page"
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      transition: 'all 0.2s ease-in-out',
                      '& fieldset': {
                        borderColor: 'grey.300',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '1.5px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '2px',
                        boxShadow: '0 0 0 3px rgba(156, 39, 176, 0.1)'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      textAlign: 'center',
                      fontWeight: 500,
                      padding: '7px 8px',
                      height: '20px',
                      lineHeight: '20px'
                    }
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  of {pagecount}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleGoToPageSubmit}
                  disabled={
                    !goToPageInput ||
                    parseInt(goToPageInput, 10) < 1 ||
                    parseInt(goToPageInput, 10) > pagecount
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 0.75,
                    height: '34px',
                    lineHeight: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Go
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {!waiterCommissionReportLoading && !allModeLoading && hasFetched && !reportVisibility && <ShowNoRecordsFound />}

      {/* Snackbar */}
      <Snackbar
        open={snackbar}
        autoHideDuration={SnackBarHideDuration}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleSnackbarClose}
        TransitionComponent={SnackBarTransition}
      >
        <Alert onClose={handleSnackbarClose} severity={severity}>
          {PopUpMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default WaiterCommissionReport
